import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import IconComponent from "../../Icons/IconComponent";
import Table from "../../Broker/Table.js";
import * as BrokerStyles from "../../Broker/Styles.js";
import adminUsersService from "../../../Endpoints/AdminUsers.js";
import ActionsMenu from "./ActionsMenu.js";
import NotificationModal from "../../Generals/Notifications/NotificationModal.js";
import { CreateAdminUserModal } from "./Modal.js";
import {
  filterAdministrationRows,
  getAdminUsersFromResponse,
  getApiMessage,
  getUserDisplayName,
  hasUsersAdminPayload,
  normalizeAdminUserRow,
} from "./helpers.js";
import * as styles from "./Styles.js";

const ADMINISTRATION_PAGE_TITLE = "Administration";
const ADMINISTRATION_PAGE_DESCRIPTION =
  "View users, their access, and configurations.";
const INITIAL_FEEDBACK = {
  open: false,
  iconType: "success",
  title: "",
  message: "",
};

const createColumn = (field, headerName, width, extraProps = {}) => ({
  field,
  headerName,
  width,
  minWidth: Math.min(width, 140),
  ...extraProps,
});

function BooleanChip({ value, trueLabel, falseLabel }) {
  const isActive = Boolean(value);

  return (
    <Chip
      label={isActive ? trueLabel : falseLabel}
      sx={isActive ? BrokerStyles.ChipBuyStyle : BrokerStyles.ChipSellStyle}
    />
  );
}

function renderFeedbackMessage(message, user, action) {
  const userName = getUserDisplayName(user);

  if (!userName) return message;

  const messageText = String(message ?? "");
  const lowerMessage = messageText.toLowerCase();
  const lowerUserName = userName.toLowerCase();
  const userNameIndex = lowerMessage.indexOf(lowerUserName);

  if (userNameIndex >= 0) {
    const beforeName = messageText.slice(0, userNameIndex);
    const nameInMessage = messageText.slice(
      userNameIndex,
      userNameIndex + userName.length
    );
    const afterName = messageText.slice(userNameIndex + userName.length);

    return (
      <>
        {beforeName}
        <strong>{nameInMessage}</strong>
        {afterName}
      </>
    );
  }

  return (
    <>
      {messageText}
      <br />
      <strong>{userName}</strong> {action}
    </>
  );
}

function AdministrationPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [feedback, setFeedback] = useState(INITIAL_FEEDBACK);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadAdminUsers = useCallback(async () => {
    setLoadingUsers(true);
    setErrorMessage("");

    const response = await adminUsersService.getAdminUsers();

    if (!response) {
      setUsers([]);
      setErrorMessage("Error loading administration users");
      setLoadingUsers(false);
      return;
    }

    const adminUsers = getAdminUsersFromResponse(response);
    const adminUserRows = adminUsers.map(normalizeAdminUserRow);

    setUsers(adminUserRows);
    setErrorMessage(
      hasUsersAdminPayload(response) && adminUserRows.length === 0
        ? "The response has users_admin, but no valid users were found"
        : ""
    );
    setLoadingUsers(false);
  }, []);

  useEffect(() => {
    loadAdminUsers();
  }, [loadAdminUsers]);


  const administrationRows = useMemo(
    () => filterAdministrationRows(users, search),
    [users, search]
  );

  const handleCreateUserClick = () => {
    setSelectedUser(null);
    setOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setSelectedUser(null);
  };

  const handleCreateUserSave = async (payload) => {
    const isEditMode = Boolean(selectedUser);
    const response = selectedUser
      ? await adminUsersService.updateAdminUser(payload)
      : await adminUsersService.createAdminUser(payload);

    if (!response) {
      const message = isEditMode
        ? "Error updating administration user"
        : "Error creating administration user";
      setErrorMessage(message);
      setFeedback({
        open: true,
        iconType: "error",
        title: isEditMode ? "Update failed" : "Create failed",
        message,
      });
      return false;
    }

    const apiMessage = getApiMessage(
      response,
      isEditMode
        ? "Administration user updated successfully"
        : "Administration user created successfully"
    );

    setFeedback({
      open: true,
      iconType: isEditMode ? "info" : "success",
      title: isEditMode ? "User updated" : "User created",
      message: renderFeedbackMessage(
        apiMessage,
        payload,
        isEditMode ? "updated" : "created"
      ),
    });
    await loadAdminUsers();
    return true;
  };

  const handleClearSearch = () => {
    setSearch("");
  };

  const handleEditUser = useCallback((row) => {
    setSelectedUser(row);
    setOpenCreateModal(true);
  }, []);

  const handleDeleteUser = useCallback((row) => {
    setUserToDelete(row);
  }, []);

  const handleCancelDeleteUser = () => {
    setUserToDelete(null);
  };

  const handleConfirmDeleteUser = useCallback(
    async (row) => {
      const user = row ?? userToDelete;
      const userId = user?.id;
      const response = await adminUsersService.deleteAdminUser(userId);

      if (!response) {
        const message = "Error deleting administration user";
        setErrorMessage(message);
        setFeedback({
          open: true,
          iconType: "error",
          title: "Delete failed",
          message,
        });
        return;
      }

      const apiMessage = getApiMessage(
        response,
        "Administration user deleted successfully"
      );

      setFeedback({
        open: true,
        iconType: "warning",
        title: "User deleted",
        message: renderFeedbackMessage(apiMessage, user, "deleted"),
      });
      setUserToDelete(null);
      await loadAdminUsers();
    },
    [loadAdminUsers, userToDelete]
  );

  const handleCloseFeedbackModal = () => {
    setFeedback(INITIAL_FEEDBACK);
  };

  const administrationColumns = useMemo(
    () => [
      createColumn("id", "ID", 90, {
        resizable: false,
      }),
      createColumn("email", "Email", 280),
      createColumn("password", "Password", 180),
      createColumn("username", "Username", 220),
      createColumn("enable", "Enable", 130, {
        resizable: false,
        renderCell: ({ value }) => (
          <BooleanChip value={value} trueLabel="Enable" falseLabel="Disable" />
        ),
      }),
      createColumn("super", "Super", 120, {
        resizable: false,
        renderCell: ({ value }) => (
          <BooleanChip value={value} trueLabel="Super" falseLabel="Standard" />
        ),
      }),
      createColumn("actions", "Actions", 100, {
        align: "center",
        resizable: false,
        renderCell: ({ row }) => (
          <ActionsMenu
            row={row}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
          />
        ),
      }),
    ],
    [handleDeleteUser, handleEditUser]
  );

  return (
    <BrokerStyles.PageRoot>
      <Box sx={styles.pageContent}>
        <Box sx={BrokerStyles.TitleWrapper}>
          <Typography sx={BrokerStyles.TitleG}>
            {ADMINISTRATION_PAGE_TITLE}
          </Typography>
          <Typography sx={BrokerStyles.Subtitle}>
            {ADMINISTRATION_PAGE_DESCRIPTION}
          </Typography>
        </Box>

        <Paper elevation={2} sx={styles.tableCard}>
          <Box sx={styles.toolbar}>
            <Box sx={styles.searchActions}>
              <TextField
                variant="outlined"
                placeholder="Search..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                size="small"
                sx={styles.searchInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="outlined"
                sx={styles.clearButton}
                onClick={handleClearSearch}
              >
                <IconComponent icon="forward_media" size="12px" /> &nbsp;Clear
              </Button>

              <Button
                variant="outlined"
                sx={styles.createUserButton}
                onClick={handleCreateUserClick}
              >
                + Create user
              </Button>
            </Box>
          </Box>

          <Table
            rows={administrationRows}
            columns={administrationColumns}
            loading={loadingUsers}
            error={errorMessage}
            checkboxSelection={false}
            enableHorizontalScroll
            tableMinWidth={1000}
            styles={BrokerStyles}
          />
        </Paper>

        <CreateAdminUserModal
          open={openCreateModal}
          onClose={handleCloseCreateModal}
          onSave={handleCreateUserSave}
          user={selectedUser}
        />

        <NotificationModal
          open={feedback.open}
          onClose={handleCloseFeedbackModal}
          iconType={feedback.iconType}
          title={feedback.title}
          message={feedback.message}
          onAcceptLabel="OK"
        />

        <NotificationModal
          open={Boolean(userToDelete)}
          onClose={handleCancelDeleteUser}
          iconType="warning"
          title="Delete user"
          message={`Are you sure you want to delete ${getUserDisplayName(userToDelete) || "this user"}?`}
          onRefuseLabel="Cancel"
          onAcceptLabel="Delete"
          onRefuse={handleCancelDeleteUser}
          onAccept={() => handleConfirmDeleteUser(userToDelete)}
        />
      </Box>
    </BrokerStyles.PageRoot>
  );
}

export default AdministrationPage;
